import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";

// MaterialUI
import { Card, Typography, Alert, Box, Container, CssBaseline, Stack, Divider } from "@mui/material";
import NavTab from "../../layout/NavTab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Interface
import { IMatriceUnit, MatriceWrapper } from "../../interface/matrice";

// React router
import { useNavigate } from "react-router-dom";

// Fetch
import { fetchPostProtect } from "../../fetchs/post";
import { getOne } from "../../fetchs/get";

// Components
import AddMatrixStep1 from "../../components/AddMatrix/AddMatrixStep1";
import AddMatrixStep2 from "../../components/AddMatrix/AddMatrixStep2";

interface IFormValues {
  name: string;
  description: string;
  client: string[] | [] | [string] | [{ _id: string; name: string }];
}

const AddMatrix = () => {
  const [error, setError] = useState<string>("");
  const [masterMatrice, setMasterMatrice] = useState<MatriceWrapper>();
  const [step, setStep] = useState<number>(1);
  const [baseMatrix, setBaseMatrix] = useState<IFormValues>({
    name: "",
    description: "",
    client: [],
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.user.token);

  const getMasterMatrice = () => {
    if (!token || masterMatrice !== undefined) return;
    getOne(token, "/matrice/master/", "project_master", setMasterMatrice, () => dispatch(sessionEnd()), setError);
  };

  useEffect(() => {
    getMasterMatrice();
  }, [token]);

  const saveMatrixBaseData = async (values: IFormValues) => {
    setBaseMatrix(values);
    setStep(2);
  };

  const saveMatrixComplete = async (values: [IMatriceUnit]) => {
    const body = {
      data: values,
      name: baseMatrix.name.trim(),
      desc: baseMatrix.description ? baseMatrix.description.trim() : "",
      clients: baseMatrix.client.map((client: any) => client._id),
    };

    if (token) {
      fetchPostProtect(token, "/matrice", JSON.stringify(body)).then((res) => {
        if (res.status === 200 || res.status === 201) {
          dispatch(showSnackbar("Matrice ajoutée avec succès", "success"));
          navigate("/matrices");
        } else {
          res.json().then((err) => {
            dispatch(showSnackbar("Erreur lors de l'ajout : " + err.error, "error"));
          });
        }
      });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      
      {/* HEADER AVEC RETOUR VERS /matrices */}
      <NavTab 
        text="Ajouter une matrice" 
        displayReturn={true} 
        matrice={true} // On peut supposer que par défaut NavTab gère le retour standard ou via une prop dédiée
      />

      <Container
        maxWidth={step === 1 ? "sm" : false}
        sx={{
          mt: 4,
          pb: 8,
          transition: "max-width 0.3s ease",
          ...(step === 2 && {
            maxWidth: "1600px",
            px: { xs: 2, md: 4 },
          }),
        }}
      >
        <Card
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "24px",
            border: "1px solid #e2e8f0",
            bgcolor: "white",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)"
          }}
        >
          {/* INDICATEUR D'ÉTAPE */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>
                {step === 1 ? "Nouvelle Matrice" : "Configuration technique"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {step === 1 
                  ? "Définissez l'identité de votre modèle de chiffrage." 
                  : `Réglages des taux pour : ${baseMatrix.name}`}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#3b82f6" }}>
                    Étape {step}/2
                </Typography>
                <Box sx={{ 
                    width: 32, height: 32, borderRadius: "50%", 
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: step === 2 ? "#3b82f6" : "#f1f5f9",
                    color: step === 2 ? "white" : "#94a3b8"
                }}>
                    {step === 2 ? <CheckCircleIcon fontSize="small" /> : <Typography variant="caption" sx={{fontWeight: 'bold'}}>1</Typography>}
                </Box>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 4, borderStyle: "dashed" }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: "100%" }}>
            {step === 1 ? (
              <AddMatrixStep1 defaultData={baseMatrix} handleSubmit={saveMatrixBaseData} />
            ) : (
              masterMatrice && (
                <AddMatrixStep2 masterMatrix={masterMatrice.data} handleSubmit={saveMatrixComplete} />
              )
            )}
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default AddMatrix;
